import { useCallback } from 'react'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'

/**
 * Where the loan screens live, and how they are moved between.
 *
 * The router registers these paths after this area is built, so nothing here can be typed against
 * the route tree yet. Rather than sprinkling casts through fifteen screens, the looseness is
 * confined to this one file: paths are built here, `useLoanNavigate` moves between them, and
 * `<LoanLink>` renders one. When the integrator wires the tree the strings below are the paths it
 * has to match, which is also why `loansRoutes` in `routes/loans/index.ts` is generated from the
 * same shapes.
 *
 * Identifiers are encoded on the way in. A loan account id is a UUID today, but a path helper
 * that assumes its input is URL-safe is a defect waiting for the first account number in a route.
 */

const segment = (value: string) => encodeURIComponent(value)

/** The operations that have a screen of their own, because each refuses a narrow display. */
export type CycleOperation =
  | 'appraise'
  | 'approve'
  | 'disburse'
  | 'repay'
  | 'settle'
  | 'waive'
  | 'restructure'
  | 'write-off'
  | 'offset'
  | 'recovery'
  | 'classify'

export const loanPaths = {
  accounts: '/loans',
  newAccount: '/loans/new',
  account: (accountId: string) => `/loans/${segment(accountId)}`,
  apply: (accountId: string) => `/loans/${segment(accountId)}/apply`,
  cycle: (accountId: string, cycleId: string) =>
    `/loans/${segment(accountId)}/cycles/${segment(cycleId)}`,
  cycleOperation: (accountId: string, cycleId: string, operation: CycleOperation) =>
    `/loans/${segment(accountId)}/cycles/${segment(cycleId)}/${operation}`,
  products: '/loans/products',
  newProduct: '/loans/products/new',
  product: (productId: string) => `/loans/products/${segment(productId)}`,
  editProduct: (productId: string) => `/loans/products/${segment(productId)}/edit`,
  portfolio: '/loans/portfolio',
  newClassificationScheme: '/loans/classification-schemes/new',
  disclosureConfig: '/loans/disclosure-config',
} as const

/**
 * Route parameters, read without a `from` route id.
 *
 * `strict: false` is the framework's own answer for a component that does not know which route
 * rendered it, which is exactly this area's position until the tree is wired. Every value is
 * optional, and the screens treat a missing one as "this link is malformed" rather than reading
 * an empty string as an identifier.
 */
export function useLoanParams(): Record<string, string | undefined> {
  return useParams({ strict: false })
}

/**
 * The raw search parameters. Every screen parses these through its own zod schema, which is what
 * turns a hand-edited or stale URL into a default rather than a thrown error.
 */
export function useRawLoanSearch(): unknown {
  return useSearch({ strict: false })
}

export interface LoanNavigateOptions {
  replace?: boolean
  search?: Record<string, unknown>
}

/** Moves between loan screens. See the note at the top for why the destination is a bare string. */
export function useLoanNavigate() {
  const navigate = useNavigate()

  return useCallback(
    (to: string, options: LoanNavigateOptions = {}) => {
      void navigate({ to, ...options } as unknown as Parameters<typeof navigate>[0])
    },
    [navigate],
  )
}
